import type { CartLine } from '@repo/shopify';

export const CART_REWARD_ATTRIBUTE_KEY = '_cart_reward';
export const CART_REWARD_ATTRIBUTE_VALUE = 'true';

export function buildRewardLineAttributes() {
	return [{ key: CART_REWARD_ATTRIBUTE_KEY, value: CART_REWARD_ATTRIBUTE_VALUE }];
}

export function isRewardManagedLine(line: Pick<CartLine, 'attributes'>) {
	return line.attributes.some(
		(attribute) =>
			attribute.key === CART_REWARD_ATTRIBUTE_KEY &&
			attribute.value === CART_REWARD_ATTRIBUTE_VALUE,
	);
}

/** Subscription gift lines (added by the buy box / gift sheet), one per order. */
export const FREE_GIFT_ATTRIBUTE_KEY = '_free_gift';

export function isFreeGiftLine(line: Pick<CartLine, 'attributes'>) {
	return line.attributes.some(
		(attribute) => attribute.key === FREE_GIFT_ATTRIBUTE_KEY && attribute.value === 'true',
	);
}

/**
 * Gift detection for the removal paths: the `_free_gift` attribute, with the
 * configured gift PRODUCT handles as a fallback for legacy lines added before
 * the attribute reached the live cart line (addItem used to drop it). Safe
 * because gift products are dedicated freebies, never sold standalone.
 */
export function isSubscriptionGiftLine(
	line: Pick<CartLine, 'attributes' | 'merchandise'>,
	giftProductHandles?: ReadonlySet<string>,
) {
	return (
		isFreeGiftLine(line) ||
		giftProductHandles?.has(line.merchandise.product.handle.toLowerCase()) === true
	);
}

/**
 * Machine-readable Crew Pack contents on the kit product's cart line: a JSON
 * array of `{ sku, handle, quantity }` covering every bag in the kit. The WMS
 * reads this off the order line to explode the kit SKU into bag picks, and
 * Recharge carries it (as a line item property) onto every renewal order.
 * Format changes must be coordinated with the WMS.
 */
export const KIT_CONTENTS_ATTRIBUTE_KEY = '_kit_contents';
/**
 * Human-readable Crew Pack contents ("2 × Fruit Punch, 1 × Lemonade…") shown
 * under the line in the cart drawer and at Shopify checkout (no leading
 * underscore, so Shopify displays it).
 */
export const KIT_CONTENTS_DISPLAY_ATTRIBUTE_KEY = 'Contents';

export function getKitContentsDisplay(line: Pick<CartLine, 'attributes'>): string | null {
	return (
		line.attributes.find((attribute) => attribute.key === KIT_CONTENTS_DISPLAY_ATTRIBUTE_KEY)
			?.value || null
	);
}

/**
 * Shared by every line of one Insider Bundle add; value = per-add uuid.
 * LEGACY: the 4-real-lines bundle model, kept so in-flight carts created
 * before the kit-product switchover still render and lock correctly.
 */
export const HYDRATE_BUNDLE_ATTRIBUTE_KEY = '_hydrate_bundle';
/**
 * Marks the bundle's bonus box: zeroed at checkout by the Shopify Buy-X-Get-Y
 * automatic discount, and kept a distinct one-time line (the attribute stops
 * Shopify merging it into a paid line of the same flavor).
 */
export const HYDRATE_BUNDLE_BONUS_ATTRIBUTE_KEY = '_hydrate_bundle_bonus';

export function isHydrateBundleBonusLine(line: Pick<CartLine, 'attributes'>) {
	return line.attributes.some(
		(attribute) =>
			attribute.key === HYDRATE_BUNDLE_BONUS_ATTRIBUTE_KEY && attribute.value === 'true',
	);
}

/**
 * Lines whose quantity stepper can remove but never stack: subscription gifts
 * (one per order) and the bundle's bonus box (one per bundle).
 */
export function isQuantityLockedLine(line: Pick<CartLine, 'attributes'>) {
	return isFreeGiftLine(line) || isHydrateBundleBonusLine(line);
}
